import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to automatically refetch data when language changes
 * This ensures that all pages update immediately when language is changed
 * 
 * @param {Function} refetchCallback - Function to call when language changes
 * @param {Array} dependencies - Additional dependencies to watch (optional)
 * 
 * @example
 * // Simple usage - just pass the fetch function
 * useLanguageChange(() => {
 *   fetchData();
 * });
 * 
 * @example
 * // With dependencies
 * useLanguageChange(() => {
 *   fetchData(currentPage);
 * }, [currentPage, dispatch]);
 */
export const useLanguageChange = (refetchCallback, dependencies = []) => {
  const { i18n } = useTranslation('global');
  const previousLanguageRef = useRef(i18n.language);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Skip on initial mount to avoid double fetch
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousLanguageRef.current = i18n.language;
      return;
    }

    // Check if language actually changed
    if (previousLanguageRef.current !== i18n.language) {
      previousLanguageRef.current = i18n.language;
      
      // Call the refetch callback
      if (typeof refetchCallback === 'function') {
        refetchCallback();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, ...dependencies]);

  // Also listen to the custom languageChanged event for immediate updates
  useEffect(() => {
    const handleLanguageChange = () => {
      if (typeof refetchCallback === 'function') {
        refetchCallback();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Hook to ensure component re-renders when language changes
 * Useful for components that only display translated text without fetching data
 * 
 * @example
 * // In a component that only displays text
 * useLanguageSync();
 */
export const useLanguageSync = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);
};

